import {
  Mutation,
  NotificationPreferenceChannel,
  Query,
  Typenames,
} from '@varo/graphql-schema-types';
import { useFocusEffect, useRouter } from 'expo-router';
import {
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { updatePushNotificationSubscriptionStatus } from 'services/engagement/engagementManager';
import { checkNotificationStatus } from 'services/engagement/notifications/notifications';
import UpdateNotificationPreferences from 'services/graphql/mutations/UpdateNotificationPreferences';
import FetchNotificationPreferences from 'services/graphql/queries/FetchNotificationPreferences';
import { type ListItemProps } from 'shared/components/vxl/ListItem/ListItem';
import { type ListItemTrailingAccessoryProps } from 'shared/components/vxl/ListItem/ListItemTrailingAccessory';
import { isWeb } from 'shared/constants/platform';
import {
  SectionedContentScreenModel,
  SectionedContentScreenSection,
} from 'shared/containers/vxl/SectionedContentScreen/SectionedContentScreen';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useFormattedCombinedError } from 'shared/hooks/errorFormatters/useFormattedCombinedError/useFormattedCombinedError';
import {
  ErrorScreenProps,
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import {
  type VXLComponent,
  VXLComponentId,
  VXLGroupId,
} from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { CombinedError, useMutation, useQuery } from 'urql';

export interface NotificationsResponse {
  sectionList: SectionedContentScreenModel;
}

interface NotificationsScreenState {
  loading: boolean;
  response?: NotificationsResponse | undefined;
  errorModel?: ErrorScreenProps | undefined;
  refresh?: () => void;
  showSnackbar: boolean;
  mobilePushNotificationStatus: boolean | undefined;
}

const formatNotificationsData = (
  notificationsData: Query | undefined,
  handleToggleStates: (id: string, value: boolean) => void,
  localToggleState: { [key: string]: boolean },
  deviceNotificationsStatus: string | null,
  setToggleState: (
    value: SetStateAction<{
      [key: string]: boolean;
    }>,
  ) => void,
): NotificationsResponse | undefined => {
  if (!notificationsData) {
    return undefined;
  }

  const listItemForTile = (
    notificationTile: NotificationPreferenceChannel,
    groupName: string,
  ): VXLComponent | undefined => {
    const groupNameString = groupName.split(' ')[0].toLowerCase();
    let trailingAccessory: ListItemTrailingAccessoryProps | undefined;
    if (!notificationTile) {
      return undefined;
    }
    const analyticsId = `${groupNameString}${notificationTile.name}${notificationTile.enabled ? 'NotificationsDisabled' : 'NotificationsEnabled'}`;

    let isOnValue = notificationTile.enabled;
    if (localToggleState[notificationTile.id] !== undefined) {
      isOnValue = localToggleState[notificationTile.id];
    } else if (
      deviceNotificationsStatus === 'denied' &&
      notificationTile.enabled
    ) {
      isOnValue = false;
      setToggleState((prevState) => ({
        ...prevState,
        [notificationTile.id]: false,
      }));
    }

    if (notificationTile.id) {
      trailingAccessory = {
        id: notificationTile.id,
        name: VXLComponentId.SWITCH,
        props: {
          isOn: isOnValue,
          onValueChange: () => {
            handleToggleStates(
              notificationTile.id,
              localToggleState[notificationTile.id] !== undefined
                ? localToggleState[notificationTile.id]
                : notificationTile.enabled,
            );
          },
          analyticsId: analyticsId,
          customAttributes: {
            push_notification_status: notificationTile.enabled ? 'off' : 'on',
          },
        },
      };
    }

    const props: ListItemProps = {
      primaryCopy: notificationTile.name,
      showsChevron: false,
      trailingAccessory,
    };

    return {
      id: notificationTile.id,
      name: VXLComponentId.LIST_ITEM,
      props,
    };
  };

  const notificationsPreferences =
    notificationsData?.myNotificationPreferences?.[
      isWeb ? 'forWeb' : 'forMobile'
    ];

  const allSections: SectionedContentScreenSection[] =
    notificationsPreferences?.map(({ id, name, description, channels }) => {
      const data =
        channels
          ?.map((channel) => listItemForTile(channel, name))
          .filter((item): item is VXLComponent => item !== undefined) || [];

      return {
        sectionHeader: {
          id,
          name: VXLGroupId.COMPONENT_GROUP,
          props: {
            components: [
              {
                id: name,
                name: VXLComponentId.SECTION_DIVIDER,
                props: { labelText: name },
              },
              {
                id: description,
                name: VXLComponentId.BODY_SM_TEXT,
                props: {
                  children: description,
                  style: {
                    backgroundColor: theme.color.background.primary,
                    paddingHorizontal: theme.spacing.m,
                    paddingVertical: theme.spacing.s,
                  },
                },
              },
            ],
          },
        },
        sectionFooter: undefined,
        data,
      };
    }) || [];

  const response = {
    sectionList: {
      sections: [...allSections],
    },
  };

  return response;
};

const useNotificationPreferences = (
  showBottomSheet: () => void,
): NotificationsScreenState => {
  const router = useRouter();
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [toggleState, setToggleState] = useState<{ [key: string]: boolean }>(
    {},
  );

  const lastMobilePushStatusRef = useRef<boolean | undefined>(undefined);

  const [
    { data: notificationsData, fetching: loading, error },
    refetchNotifications,
  ] = useQuery<Query>({
    query: FetchNotificationPreferences,
    requestPolicy: 'cache-and-network',
  });

  const getPushChannelEnabled = useCallback(() => {
    if (!notificationsData?.myNotificationPreferences?.forMobile) {
      return;
    }
    const group = notificationsData.myNotificationPreferences.forMobile.find(
      (g) => g.name.toLowerCase() === 'marketing notifications',
    );
    const channel = group?.channels.find(
      (c) => c.name.toLowerCase() === 'push',
    );
    return channel?.enabled;
  }, [notificationsData]);

  useEffect(() => {
    const isEnabled = getPushChannelEnabled();
    if (
      isEnabled === undefined ||
      lastMobilePushStatusRef.current === isEnabled
    ) {
      return;
    }

    lastMobilePushStatusRef.current = isEnabled;
    updatePushNotificationSubscriptionStatus(isEnabled);
  }, [getPushChannelEnabled]);

  const [systemSettingsStatus, setSystemSettingsStatus] = useState<
    string | null
  >(null);

  useFocusEffect(
    useCallback(() => {
      const fetchNotificationStatus = async () => {
        const status = await checkNotificationStatus();
        setSystemSettingsStatus(status);
      };
      void fetchNotificationStatus();
    }, []),
  );

  const { defaultTitle, defaultMessage, retryButtonTitle, cancelButtonTitle } =
    useDefaultErrorStrings();

  const [{ error: mutationError }, executeMutation] = useMutation<Mutation>(
    UpdateNotificationPreferences,
  );

  const responseError = useFormattedCombinedError(
    error || (mutationError as CombinedError | undefined),
  );
  const refresh = () => {
    refetchNotifications({ requestPolicy: 'network-only' });
  };
  const cancel = () => {
    router.back();
  };

  const buttons = {
    primaryButton: {
      title: retryButtonTitle,
      onPress: refresh,
    },
    secondaryButton: {
      title: cancelButtonTitle,
      onPress: cancel,
    },
  };

  const errorModel =
    error || mutationError
      ? {
          screenType: ErrorScreenType.Error,
          messageHeadline: responseError?.title ?? defaultTitle,
          messageBody: responseError?.message ?? defaultMessage,
          ...buttons,
        }
      : undefined;

  const handleToggleStates = async (id: string, currentValue: boolean) => {
    try {
      const notificationSettingsStatus = await checkNotificationStatus();

      if (notificationSettingsStatus === 'denied' && !currentValue && !isWeb) {
        showBottomSheet();
      } else {
        setToggleState((prevState) => ({
          ...prevState,
          [id]: !currentValue,
        }));
        setShowSnackbar(false);
        updatePushNotificationSubscriptionStatus(!currentValue);
        lastMobilePushStatusRef.current = !currentValue;
        const response = await executeMutation({
          input: { id: id, enabled: !currentValue },
        });

        if (
          response?.data?.updateNotificationPreferenceChannel.__typename ===
          Typenames.UpdateNotificationPreferenceChannelError
        ) {
          setShowSnackbar(true);
          setToggleState((prevState) => ({
            ...prevState,
            [id]: currentValue,
          }));
          updatePushNotificationSubscriptionStatus(currentValue);
          lastMobilePushStatusRef.current = currentValue;
        } else {
          setShowSnackbar(false);
        }
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const response = formatNotificationsData(
    notificationsData,
    handleToggleStates,
    toggleState,
    systemSettingsStatus,
    setToggleState,
  );

  return {
    refresh,
    response,
    mobilePushNotificationStatus: getPushChannelEnabled(),
    loading,
    errorModel,
    showSnackbar,
  };
};

export default useNotificationPreferences;
